import type { CategoryModelV2, ManufacturerModelV2, ProductModelV2 } from '@develop/ia-api-library'
import { isString } from '@develop/ia-utils'
import type { ProductParams } from '@/models/middleware-types'
import type { PharmacyDetails } from '~/models/pharmacy-details'
import { ProductSearchQueryParam } from '~/models/product-search'

export type PharmacyWithSlug = {
  additionalInformation: Partial<PharmacyDetails['additionalInformation']> & { slug: PharmacyDetails['additionalInformation']['slug'] }
}
export type PharmacyWithId = Pick<PharmacyDetails, 'id'>

export type ProductWithSlug = Pick<ProductModelV2, 'slug' | 'pzn'>

/**
 * Utility functions for constructing pharmacy/non-pharmacy routes by passing props
 */
export const useRouteUtils = () => {
  const router = useRouter()
  const resolveRoute = (to: RouteLocationRaw) => {
    return router.resolve(to)
  }

  const getPharmacyRoute = (pharmacyOrSlug?: PharmacyWithSlug | string | null) => {
    if (!pharmacyOrSlug) {
      return resolveRoute('/')
    }

    const slug = isString(pharmacyOrSlug) ? pharmacyOrSlug : pharmacyOrSlug.additionalInformation.slug
    const params = convertPharmacySlugToParams(slug, { ensureRouteParamsType: true })!

    return resolveRoute({
      name: resolvePharmacyRouteName('apotheke-pharmacyName-pharmacyPlz-pharmacyId', params),
      params,
    })
  }

  const getGeneralProductSearchRoute = (searchQuery?: string) => {
    return resolveRoute({
      name: 'suche',
      query: { search: searchQuery },
    })
  }

  const getProductSearchRoute = (pharmacy?: PharmacyWithSlug | null, searchQuery?: string) => {
    if (!pharmacy) {
      return resolveRoute({
        name: 'produkte',
        query: { search: searchQuery },
      })
    }

    const slug = pharmacy.additionalInformation.slug
    const params = convertPharmacySlugToParams(slug, { ensureRouteParamsType: true })!

    return resolveRoute({
      name: resolvePharmacyRouteName('pharmacyName-pharmacyPlz-pharmacyId-produkte', params),
      params,
      query: { search: searchQuery },
    })
  }

  const getProductSearchCategoryRoute = (category: CategoryModelV2, pharmacy?: PharmacyWithSlug | null) => {
    const categoryValue = `${category.slug}-${category.id}`

    if (pharmacy) {
      const pharmacySlug = pharmacy.additionalInformation.slug
      const pharmacyParams = convertPharmacySlugToParams(pharmacySlug, { ensureRouteParamsType: true })!

      return resolveRoute({
        name: resolvePharmacyRouteName('pharmacyId-category', pharmacyParams),

        params: {
          ...pharmacyParams,
          slug: categoryValue,
        },
      })
    }

    return resolveRoute({
      name: 'category',
      params: {
        slug: categoryValue,
      },
    })
  }

  const getProductSearchOffersRoute = (pharmacy?: PharmacyWithSlug | null) => {
    const productSearchRoute = getProductSearchRoute(pharmacy)
    return resolveRoute({
      ...productSearchRoute,
      query: {
        [ProductSearchQueryParam.offers]: 'true',
      },
    })
  }

  const getProductSearchManufacturerRoute = (manufacturer: ManufacturerModelV2, pharmacy?: PharmacyWithSlug | null) => {
    if (pharmacy) {
      const pharmacySlug = pharmacy.additionalInformation.slug
      const pharmacyParams = convertPharmacySlugToParams(pharmacySlug, { ensureRouteParamsType: true })!

      return resolveRoute({
        name: resolvePharmacyRouteName('pharmacyId-manufacturer', pharmacyParams),

        params: {
          ...pharmacyParams,
          slug: manufacturer.slug,
        },
      })
    }

    return resolveRoute({
      name: 'manufacturer',
      params: {
        slug: manufacturer.slug,
      },
    })
  }

  const getPrescriptionUploadRoute = (pharmacy?: PharmacyWithSlug | null) => {
    if (!pharmacy) {
      return resolveRoute({
        name: 'rezept-upload',
      })
    }

    const slug = pharmacy.additionalInformation.slug
    const params = convertPharmacySlugToParams(slug, { ensureRouteParamsType: true })!

    return resolveRoute({
      name: resolvePharmacyRouteName('pharmacyName-pharmacyPlz-pharmacyId-rezept-upload', params),
      params,
    })
  }

  const getProductRoute = (product: ProductWithSlug | ProductParams, pharmacy?: PharmacyWithSlug | null) => {
    const isProductParamsType = 'productName' in product

    // Handle regular product object with slug, as well as ProductParams without slug
    const productParams = isProductParamsType
      ? product
      : {
          productName: product.slug.product,
          pzn: product.pzn,
        }

    const noPharmacyRoute = resolveRoute({
      name: 'produkt-productName-pzn',
      params: productParams,
    })

    if (!pharmacy || (!isProductParamsType && !product.slug.pharmacy)) {
      return noPharmacyRoute
    }

    const pharmacySlug = pharmacy
      ? pharmacy.additionalInformation.slug
      : !isProductParamsType ? product.slug.pharmacy : null

    if (!pharmacySlug) {
      return noPharmacyRoute
    }

    const pharmacyParams = convertPharmacySlugToParams(pharmacySlug, { ensureRouteParamsType: true })!

    return resolveRoute({
      name: resolvePharmacyRouteName('pharmacyName-pharmacyPlz-pharmacyId-produkt-productName-pzn', pharmacyParams),
      params: {
        ...productParams,
        ...pharmacyParams,
      },
    })
  }

  return {
    // utils
    convertPharmacySlugToParams,
    // routes
    getPharmacyRoute,
    getGeneralProductSearchRoute,
    getProductSearchRoute,
    getProductSearchCategoryRoute,
    getProductSearchOffersRoute,
    getProductSearchManufacturerRoute,
    getProductRoute,
    getPrescriptionUploadRoute,
  }
}

// Composables are to be used for navigating, ensuring correct route taking into account the selected pharmacy

export const useHomepageRoute = () => {
  const { getPharmacyRoute } = useRouteUtils()
  const currentPharmacyStore = useCurrentPharmacyStore()

  return computed(() => getPharmacyRoute(currentPharmacyStore.pharmacy))
}

export const useProductSearchRoute = (searchQuery?: MaybeRefOrGetter<string>) => {
  const { getProductSearchRoute } = useRouteUtils()
  const { pharmacy } = storeToRefs(useCurrentPharmacyStore())

  return computed(() => getProductSearchRoute(pharmacy.value, toValue(searchQuery)))
}

export const useGeneralProductSearchRoute = (searchQuery?: MaybeRefOrGetter<string>) => {
  const { getGeneralProductSearchRoute } = useRouteUtils()

  return computed(() => getGeneralProductSearchRoute(toValue(searchQuery)))
}

export const useProductSearchCategoryRoute = (category: MaybeRefOrGetter<CategoryModelV2>) => {
  const { getProductSearchCategoryRoute } = useRouteUtils()
  const { pharmacy } = storeToRefs(useCurrentPharmacyStore())

  return computed(() => getProductSearchCategoryRoute(toValue(category), pharmacy.value))
}

export const useProductSearchOffersRoute = () => {
  const { getProductSearchOffersRoute } = useRouteUtils()
  const { pharmacy } = storeToRefs(useCurrentPharmacyStore())

  return computed(() => getProductSearchOffersRoute(pharmacy.value))
}

export const useProductSearchManufacturerRoute = (manufacturer: MaybeRefOrGetter<ManufacturerModelV2>) => {
  const { getProductSearchManufacturerRoute } = useRouteUtils()
  const { pharmacy } = storeToRefs(useCurrentPharmacyStore())

  return computed(() => getProductSearchManufacturerRoute(toValue(manufacturer), pharmacy.value))
}

export const useProductRoute = (product: MaybeRefOrGetter<ProductWithSlug>) => {
  const { getProductRoute } = useRouteUtils()
  const { pharmacy } = storeToRefs(useCurrentPharmacyStore())

  return computed(() => getProductRoute(toValue(product), pharmacy.value))
}

export const usePrescriptionUploadRoute = () => {
  const { getPrescriptionUploadRoute } = useRouteUtils()
  const { pharmacy } = storeToRefs(useCurrentPharmacyStore())

  return computed(() => getPrescriptionUploadRoute(pharmacy.value))
}
